import React, { useMemo, useState } from "react";
import { PasswordField } from "@/components/ui/PasswordField";
import { useToastStore } from "@/store/toastStore";
import { assertOk, toUserErrorMessage } from "@/api/apiError";
import { normalizeToE164 } from "@/lib/phoneRu";

type Strength = 0 | 1 | 2 | 3 | 4;

function computeStrength(p: string): { score: Strength; hints: string[] } {
    const s = p.trim();
    const hints: string[] = [];
    let score = 0;

    if (s.length >= 8) score++;
    else hints.push("Минимум 8 символов");

    if (/[a-z]/.test(s) && /[A-Z]/.test(s)) score++;
    else hints.push("Добавь верхний и нижний регистр (Aa)");

    if (/\d/.test(s)) score++;
    else hints.push("Добавь цифру");

    if (/[^A-Za-z0-9]/.test(s)) score++;
    else hints.push("Добавь спецсимвол (!@#…)");

    if (/^(password|qwerty|12345678|00000000)$/i.test(s)) {
        score = 1;
        hints.unshift("Пароль слишком простой");
    }

    return { score: score as Strength, hints };
}

function strengthLabel(score: Strength) {
    switch (score) {
        case 0:
        case 1:
            return "Слабый";
        case 2:
            return "Средний";
        case 3:
            return "Хороший";
        case 4:
            return "Сильный";
    }
}

type Props = {
    phoneNumber: string;
    oldPasswordPrefill?: string;
    onCancel?: () => void;
    onSuccess?: (newPassword: string) => void;
    apiBaseUrl?: string; // default "/api"
};

export const ChangePasswordForm: React.FC<Props> = ({
                                                        phoneNumber,
                                                        oldPasswordPrefill = "",
                                                        onCancel,
                                                        onSuccess,
                                                        apiBaseUrl = "/api",
                                                    }) => {
    const [oldPassword, setOldPassword] = useState(oldPasswordPrefill);
    const [newPassword, setNewPassword] = useState("");
    const [newPassword2, setNewPassword2] = useState("");
    const [loading, setLoading] = useState(false);

    // ✅ твой реальный API
    const pushToast = useToastStore((s) => s.push);

    const toast = (type: "success" | "error" | "info", message: string, title?: string) => {
        pushToast({
            type,
            title,
            message,
            durationMs: type === "error" ? 4500 : 3200,
        });
    };

    const { score, hints } = useMemo(() => computeStrength(newPassword), [newPassword]);

    const mismatch = useMemo(() => {
        const p1 = newPassword.trim();
        const p2 = newPassword2.trim();
        return p2.length > 0 && p1 !== p2;
    }, [newPassword, newPassword2]);

    const sameAsOld = useMemo(() => {
        const p1 = newPassword.trim();
        const old = oldPassword.trim();
        return p1.length > 0 && old.length > 0 && p1 === old;
    }, [newPassword, oldPassword]);

    const canSubmit = useMemo(() => {
        const old = oldPassword.trim();
        const p1 = newPassword.trim();
        const p2 = newPassword2.trim();

        if (!old || !p1 || !p2) return false;
        if (p1 !== p2) return false;
        if (p1.length < 8) return false;
        if (p1 === old) return false;

        // достаточно "средний" (>= 2)
        return score >= 2;
    }, [oldPassword, newPassword, newPassword2, score]);

    const clear = () => {
        setOldPassword(oldPasswordPrefill);
        setNewPassword("");
        setNewPassword2("");
        toast("info", "Форма очищена");
    };

    const handleSubmit = async () => {
        if (!canSubmit) {
            // ✅ дружелюбная подсказка, без хард-валидаций на бэке
            let msg = "Проверьте поля пароля";
            if (!oldPassword.trim()) msg = "Введите текущий пароль";
            else if (newPassword.trim().length < 8) msg = "Новый пароль: минимум 8 символов";
            else if (newPassword.trim() === oldPassword.trim()) msg = "Новый пароль не должен совпадать со старым";
            else if (newPassword.trim() !== newPassword2.trim()) msg = "Пароли должны совпадать";
            else if (score < 2) msg = "Сделайте пароль чуть сильнее (хотя бы “Средний”)";

            toast("info", msg);
            return;
        }

        setLoading(true);

        try {
            const res = await fetch(`${apiBaseUrl}/auth/change-password`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({
                    phoneNumber: normalizeToE164(phoneNumber),
                    oldPassword: oldPassword.trim(),
                    newPassword: newPassword.trim(),
                }),
            });

            await assertOk(res, "auth");

            toast("success", "Пароль успешно изменён", "Готово");
            onSuccess?.(newPassword.trim());

            setOldPassword(oldPasswordPrefill);
            setNewPassword("");
            setNewPassword2("");
        } catch (e) {
            // Никогда не показываем сырой backend-текст: helper уже мапит в русский.
            toast("error", toUserErrorMessage(e, "auth"), "Ошибка");
        } finally {
            setLoading(false);
        }
    };

    const scoreColor =
        score <= 1 ? "text-red-300" : score === 2 ? "text-amber-300" : "text-emerald-300";
    const barColor =
        score <= 1 ? "bg-red-400/70" : score === 2 ? "bg-amber-400/70" : "bg-emerald-400/70";

    return (
        <div className="space-y-3">
            <div className="text-[11px] text-slate-400">
                Телефон: <span className="text-slate-200">{phoneNumber}</span>
            </div>

            <PasswordField
                label="Текущий пароль"
                value={oldPassword}
                onChange={setOldPassword}
                placeholder="Введите текущий пароль"
                autoComplete="current-password"
            />

            <PasswordField
                label="Новый пароль"
                value={newPassword}
                onChange={setNewPassword}
                placeholder="Придумайте новый пароль"
                autoComplete="new-password"
                errorText={sameAsOld ? "Новый пароль не должен совпадать со старым" : null}
            />

            {/* strength meter */}
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 px-3 py-2">
                <div className="flex items-center justify-between">
                    <div className="text-[11px] text-slate-300">
                        Надёжность:{" "}
                        <span className={["font-semibold", scoreColor].join(" ")}>{strengthLabel(score)}</span>
                    </div>
                    <div className="text-[10px] text-slate-500">{newPassword.trim().length} символов</div>
                </div>

                <div className="mt-2 h-1.5 w-full rounded-full bg-slate-800 overflow-hidden">
                    <div
                        className={["h-full rounded-full transition-all", barColor].join(" ")}
                        style={{ width: `${(score / 4) * 100}%` }}
                    />
                </div>

                {newPassword.trim().length > 0 && hints.length > 0 && (
                    <ul className="mt-2 space-y-1 text-[10px] text-slate-400 list-disc pl-4">
                        {hints.slice(0, 3).map((h) => (
                            <li key={h}>{h}</li>
                        ))}
                    </ul>
                )}

                <div className="mt-2 text-[10px] text-slate-500">
                    Рекомендуем: 8+ символов, Aa, цифры и спецсимвол.
                </div>
            </div>

            <PasswordField
                label="Повторите новый пароль"
                value={newPassword2}
                onChange={setNewPassword2}
                placeholder="Повторите пароль"
                autoComplete="new-password"
                errorText={mismatch ? "Пароли не совпадают" : null}
            />

            <div className="flex gap-2 justify-end pt-1">
                <button
                    type="button"
                    onClick={clear}
                    disabled={loading}
                    className="rounded-xl border border-slate-700 px-4 py-2 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-60"
                >
                    Очистить
                </button>

                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={loading}
                        className="rounded-xl border border-slate-700 px-4 py-2 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-60"
                    >
                        Назад
                    </button>
                )}

                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!canSubmit || loading}
                    className="rounded-xl bg-sky-500 px-4 py-2 text-xs font-medium text-white hover:bg-sky-600 disabled:opacity-60"
                >
                    {loading ? "Сохраняем..." : "Сменить пароль"}
                </button>
            </div>
        </div>
    );
};
